import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Switch,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import PropTypes from "prop-types";
import Button3px from "../../Generals/Buttons/3pxButton";
import TitledInput from "../../Generals/Inputs/TitledInput";
import TitledSelector from "../../Generals/Selectors/TitledSelector";
import TextInputWithCounter from "../Inputs/TitledBigInput";
import Colors from "../../../assets/Variables/ColorsVariables.js";

import {
  dialogSx,
  dialogPaperSx,
  headerTitleSx,
  visibilityLabelSx,
  closeButtonSx,
  messageSx,
  drawdownLabelSx,
  actionsSx,
  cancelButtonSx,
  continueButtonProps,
} from "./FormModalStyles.js";

const FormSystemModal = (props) => {
  const {
    open,
    title,
    message,
    fields,
    onSubmit,
    onClose,
    type,
    confirmLabel = "Save Changes",
    cancelLabel = "Cancel",
    showVisibility = false,
    visibilityValue = false,
    onChangeVisibility,
    mode = "signal",
    variant = "edit",
  } = props;

  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!fields) return;

    const initial = {};
    fields.forEach((f) => {
      initial[f.name] = f.value ?? "";
    });

    if (showVisibility) {
      initial.visibility_switch = visibilityValue ?? false;
    }

    if (!initial.drawdown_mode) {
      initial.drawdown_mode = "NoDrawdown";
    }

    if (
      mode === "signal" &&
      variant === "create" &&
      !initial.ib_account &&
      initial.metatrader_login
    ) {
      initial.ib_account = initial.metatrader_login;
    }

    setFormState(initial);
    setErrors({});
  }, [fields, mode, variant, open, showVisibility, visibilityValue]);

  const getField = (name) => fields?.find((f) => f.name === name);

  const drawdownOptions = [
    { value: "NoDrawdown", label: "No Drawdown" },
    { value: "InEquity", label: "In Equity" },
    { value: "InBalance", label: "In Balance" },
  ];

  const isDrawdownDisabled = formState.drawdown_mode === "NoDrawdown";

  const isFieldRequired = (fieldName) => {
    if (mode === "signal") {
      if (fieldName === "strategy_description") return false;
      if (["drawdown_amount", "disable_on_drawdown"].includes(fieldName)) {
        return !isDrawdownDisabled;
      }
      return true;
    }

    if (mode === "follower") {
      if (fieldName === "drawdown_amount") {
        return !isDrawdownDisabled;
      }

      if (["min_lot_size", "max_lot_size"].includes(fieldName)) {
        return false;
      }

      return true;
    }

    return false;
  };

  const isEmptyValue = (value) =>
    value === "" || value === null || value === undefined;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormState((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (
        mode === "signal" &&
        variant === "create" &&
        name === "metatrader_login" &&
        (!prev.ib_account || prev.ib_account === prev.metatrader_login)
      ) {
        next.ib_account = value;
      }

      return next;
    });

    if (!isEmptyValue(value)) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleDrawdownChange = (event) => {
    const newValue = event.target.value;
    if (!newValue) return;

    setFormState((prev) => ({
      ...prev,
      drawdown_mode: newValue,
      ...(newValue === "NoDrawdown" && {
        drawdown_amount: "",
        disable_on_drawdown: "",
      }),
    }));

    setErrors((prev) => ({
      ...prev,
      drawdown_mode: false,
      drawdown_amount: false,
      disable_on_drawdown: false,
    }));
  };

  const handleSubmit = () => {
    if (!fields?.length) {
      if (onSubmit) onSubmit(formState);
      return;
    }

    const newErrors = {};

    fields.forEach((field) => {
      if (isFieldRequired(field.name) && isEmptyValue(formState[field.name])) {
        newErrors[field.name] = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (onSubmit) onSubmit(formState);
  };

  const getFieldTitle = (field) =>
    `${field.label}${isFieldRequired(field.name) ? " *" : ""}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableRestoreFocus
      sx={{
        ...dialogSx,
        ...(mode === "signal" && {
          "& .MuiDialog-paper": {
            maxHeight: "75vh",
          },
        }),
        ...(mode === "follower" && {
          "& .MuiDialog-paper": {
            maxHeight: "75vh",
          },
        }),
      }}
      PaperProps={{
        sx: {
          ...dialogPaperSx,
          ...(mode === "signal" && variant === "edit" && {
            maxWidth: 620,
            height: "720px",
          }),
          ...(mode === "signal" && variant === "create" && {
            maxWidth: 620,
            height: "820px",
          }),
          ...(mode === "follower" && variant === "edit" && {
            maxWidth: 549,
            height: "610px",
          }),
          ...(mode === "follower" && variant === "create" && {
            maxWidth: 549,
            height: "690px",
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          pt: 1,
        }}
      >
        <Typography sx={headerTitleSx}>{title}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {showVisibility && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={visibilityLabelSx}>
                {mode === "signal" ? "Visibility" : "Copying"}
              </Typography>
              <Switch
                checked={!!formState.visibility_switch}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormState((prev) => ({
                    ...prev,
                    visibility_switch: checked,
                  }));

                  if (onChangeVisibility) {
                    onChangeVisibility(e);
                  }
                }}
                color="primary"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: Colors.Success_300,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#CBD5F5",
                  },
                }}
              />
            </Box>
          )}
          <Button onClick={onClose} sx={closeButtonSx}>
            ×
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 1,
          pb: 0,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {message && <Typography sx={messageSx}>{message}</Typography>}

        {fields && type === "form" && (
          <>
            {mode === "signal" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {(() => {
                  const field = getField("metatrader_login");
                  if (!field) return null;
                  return (
                    <TitledInput
                      name={field.name}
                      title={getFieldTitle(field)}
                      value={formState[field.name] || ""}
                      handleChange={handleChange}
                      placeholder={field.placeholder || ""}
                      type={field.type || "number"}
                      required={isFieldRequired(field.name)}
                      error={!!errors[field.name]}
                      helperText={errors[field.name] ? "This field is required" : ""}
                    />
                  );
                })()}

                {(() => {
                  const field = getField("strategy_name");
                  if (!field) return null;
                  return (
                    <TitledInput
                      name={field.name}
                      title={getFieldTitle(field)}
                      value={formState[field.name] || ""}
                      handleChange={handleChange}
                      placeholder={field.placeholder || ""}
                      type={field.type || "text"}
                      required={isFieldRequired(field.name)}
                      error={!!errors[field.name]}
                      helperText={errors[field.name] ? "This field is required" : ""}
                    />
                  );
                })()}

                {(() => {
                  const field = getField("strategy_description");
                  if (!field) return null;
                  const value = formState[field.name] || "";
                  return (
                    <TextInputWithCounter
                      name={field.name}
                      title={getFieldTitle(field)}
                      value={value}
                      type="text"
                      handleChange={(e) => {
                        const { value } = e.target;
                        setFormState((prev) => ({
                          ...prev,
                          [field.name]: value,
                        }));
                        if (!isEmptyValue(value)) {
                          setErrors((prev) => ({
                            ...prev,
                            [field.name]: false,
                          }));
                        }
                      }}
                      maxChars={300}
                      placeholder="Enter a short description"
                      error={!!errors[field.name]}
                      helperText={errors[field.name] ? "This field is required" : ""}
                    />
                  );
                })()}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1.3fr 1fr",
                    gap: 1.5,
                  }}
                >
                  {(() => {
                    const field = getField("strategy_type");
                    if (!field) return null;
                    return (
                      <TitledSelector
                        title={getFieldTitle(field)}
                        name={field.name}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        options={field.options || []}
                        placeholder="Select strategy type"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "Please select an option" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("fee_value_percentage");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("ib_account");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={drawdownLabelSx}>
                    Drawdown Protection
                  </Typography>

                  <RadioGroup
                    row
                    value={formState.drawdown_mode || "NoDrawdown"}
                    onChange={handleDrawdownChange}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      {drawdownOptions.map((opt) => (
                        <Box
                          key={opt.value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "8px",
                            border: `1.5px solid ${(formState.drawdown_mode || "NoDrawdown") === opt.value
                              ? Colors.Secondary_600
                              : Colors.Gray_200
                              }`,
                            px: "10px",
                            py: "10px",
                            width: "145px",
                            height: "50px",
                            boxSizing: "border-box",
                          }}
                        >
                          <FormControlLabel
                            value={opt.value}
                            control={
                              <Radio
                                sx={{
                                  color: Colors.Gray_400,
                                  "&.Mui-checked": {
                                    color: Colors.Secondary_600,
                                  },
                                }}
                              />
                            }
                            label={opt.label}
                            sx={{
                              m: 0,
                              width: "100%",
                              ".MuiFormControlLabel-label": {
                                fontFamily: "Plus Jakarta Sans, sans-serif",
                                fontWeight: 400,
                                fontSize: "10.32px",
                                lineHeight: 1.4,
                                color: Colors.Secondary_700,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </RadioGroup>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  {(() => {
                    const field = getField("drawdown_amount");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        disabled={isDrawdownDisabled}
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("disable_on_drawdown");
                    if (!field) return null;
                    return (
                      <TitledSelector
                        title={getFieldTitle(field)}
                        name={field.name}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        options={field.options || []}
                        placeholder="Select option"
                        disabled={isDrawdownDisabled}
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "Please select an option" : ""}
                      />
                    );
                  })()}
                </Box>
              </Box>
            )}

            {mode === "follower" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  {(() => {
                    const field = getField("follower_login");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type={field.type || "number"}
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("signal_login");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type={field.type || "number"}
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  {(() => {
                    const field = getField("copy_mode");
                    if (!field) return null;
                    return (
                      <TitledSelector
                        title={getFieldTitle(field)}
                        name={field.name}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        options={field.options || []}
                        placeholder="Select copy mode"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "Please select an option" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("risk_ratio");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  {(() => {
                    const field = getField("min_lot_size");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}

                  {(() => {
                    const field = getField("max_lot_size");
                    if (!field) return null;
                    return (
                      <TitledInput
                        name={field.name}
                        title={getFieldTitle(field)}
                        value={formState[field.name] || ""}
                        handleChange={handleChange}
                        type="number"
                        required={isFieldRequired(field.name)}
                        error={!!errors[field.name]}
                        helperText={errors[field.name] ? "This field is required" : ""}
                      />
                    );
                  })()}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={drawdownLabelSx}>
                    Drawdown Protection
                  </Typography>

                  <RadioGroup
                    row
                    value={formState.drawdown_mode || "NoDrawdown"}
                    onChange={handleDrawdownChange}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      {drawdownOptions.map((opt) => (
                        <Box
                          key={opt.value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "8px",
                            border: `1.5px solid ${(formState.drawdown_mode || "NoDrawdown") === opt.value
                              ? Colors.Secondary_600
                              : Colors.Gray_200
                              }`,
                            px: "10px",
                            py: "10px",
                            width: "145px",
                            height: "50px",
                            boxSizing: "border-box",
                          }}
                        >
                          <FormControlLabel
                            value={opt.value}
                            control={
                              <Radio
                                sx={{
                                  color: Colors.Gray_400,
                                  "&.Mui-checked": {
                                    color: Colors.Secondary_600,
                                  },
                                }}
                              />
                            }
                            label={opt.label}
                            sx={{
                              m: 0,
                              width: "100%",
                              ".MuiFormControlLabel-label": {
                                fontFamily: "Plus Jakarta Sans, sans-serif",
                                fontWeight: 400,
                                fontSize: "10.32px",
                                lineHeight: 1.4,
                                color: Colors.Secondary_700,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </RadioGroup>
                </Box>

                {(() => {
                  const field = getField("drawdown_amount");
                  if (!field) return null;
                  return (
                    <TitledInput
                      name={field.name}
                      title={getFieldTitle(field)}
                      value={formState[field.name] || ""}
                      handleChange={handleChange}
                      type="number"
                      disabled={isDrawdownDisabled}
                      required={isFieldRequired(field.name)}
                      error={!!errors[field.name]}
                      helperText={errors[field.name] ? "This field is required" : ""}
                    />
                  );
                })()}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={actionsSx}>
        {cancelLabel && (
          <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
            {cancelLabel}
          </Button>
        )}

        <Button3px
          HandleOnClick={type === "form" ? handleSubmit : onClose}
          label={confirmLabel}
          {...continueButtonProps}
        />
      </DialogActions>
    </Dialog>
  );
};

FormSystemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  fields: PropTypes.array,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["form", "message"]).isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  showVisibility: PropTypes.bool,
  visibilityValue: PropTypes.bool,
  onChangeVisibility: PropTypes.func,
  mode: PropTypes.oneOf(["signal", "follower"]),
  variant: PropTypes.oneOf(["edit", "create"]),
};

export default FormSystemModal;